import { createServerFn } from "@tanstack/react-start"
import { listDestinations, getDestination } from "@/lib/destinations.server"
import type { ExploreCardItem } from "@/components/ExploreCard"

// Client-safe surface for the destination hubs: the thin-content threshold,
// the row types, and the createServerFn wrappers. The db-touching reads live in
// `destinations.server.ts`, imported ONLY inside the handlers below so the
// compiler strips them from the client bundle. This module is imported by
// client code, so it must never pull in `@/db/connection`/dotenv — that crashed
// hydration on every page.

// Thin-content gate. A destination hub only exists once it has enough published
// guides to be a real page — below this it's a doorway/thin page that hurts the
// whole site's standing, so it gets neither a hub nor a sitemap entry. This list
// is the single source of truth for which hubs exist AND which are in the sitemap.
export const MIN_DESTINATION_GUIDES = 3

export type DestinationSummary = {
  destination: string
  slug: string
  guideCount: number
  image_url: string | null
}

export type DestinationDetail = {
  destination: string
  guides: ExploreCardItem[]
}

// SSR data source for the /destinations hub index. Direct Postgres (never a
// self-fetch to /api/trpc — deadlocks; see guide-meta.ts), no paid client.
export const fetchDestinations = createServerFn({ method: `GET` }).handler(
  async (): Promise<DestinationSummary[]> => {
    return listDestinations().catch(() => [])
  }
)

// SSR data source for a single /destinations/<slug> hub. Resolves to null for a
// genuine miss (unknown slug / below threshold) but lets a DB error PROPAGATE so
// the route loader can tell a transient blip from a real 404 — never noindexing
// a real hub on a transient error.
export const fetchDestination = createServerFn({ method: `GET` })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }): Promise<DestinationDetail | null> => {
    return getDestination(data.slug)
  })
